import { css } from '@emotion/react';
import { BadRequest } from '@weave/schema-gen-ts/dist/google/rpc/error_details.pb';
import { Status } from '@weave/schema-gen-ts/dist/google/rpc/status.pb';
import { AppointmentWriteBackRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/writeback.pb';
import { HttpError } from '@frontend/fetch';
import { i18next, useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';

export type BackendError = {
  message: string;
  violations: { field?: string; description?: string; hint?: string }[];
};

const FIELD_HINTS: Record<string, string> = {
  [('externalStatus' satisfies keyof AppointmentWriteBackRequest).toLowerCase()]: i18next.t(
    "Ensure a status has been mapped to the 'Confirmed' Weave Status in Organization Settings > Data Sync",
    { ns: 'scheduleCalendarRequest' }
  ),
  [('operatoryExternalId' satisfies keyof AppointmentWriteBackRequest).toLowerCase()]: i18next.t(
    'Select a workstation (operatory) before approving this request.',
    { ns: 'scheduleCalendarRequest' }
  ),
  'create_appointment.operatory_id': i18next.t('Select a workstation (operatory) before approving this request.', {
    ns: 'scheduleCalendarRequest',
  }),
  operatoryid: i18next.t('Select a workstation (operatory) before approving this request.', {
    ns: 'scheduleCalendarRequest',
  }),
};

export const EMPTY_BACKEND_ERROR: BackendError = { message: '', violations: [] };

const formatFieldName = (field: string): string => field.replace(/([A-Z])/g, ' $1').trim();

export const extractBackendError = (error: unknown): BackendError => {
  if (!(error instanceof HttpError)) return EMPTY_BACKEND_ERROR;
  const data = (error.data ?? {}) as Status;

  const message = data.message ?? '';
  const violations = (data.details?.flatMap((d) => (d as BadRequest).fieldViolations ?? []) ?? [])
    .filter((v) => v.field || v.description)
    .map((v) => ({
      field: v.field ? formatFieldName(v.field) : undefined,
      description: v.description,
      hint: v.field ? FIELD_HINTS[v.field.toLowerCase()] : undefined,
    }));

  return { message, violations };
};

type BackendErrorDetailsProps = {
  backendError: BackendError;
};

export const BackendErrorDetails = ({ backendError }: BackendErrorDetailsProps) => {
  const { t } = useTranslation('scheduleCalendarRequest');
  const { message, violations } = backendError;

  if (!message && !violations.length) return null;

  return (
    <div css={containerStyle}>
      <Text weight='bold' size='medium' as='h3'>
        {t('Error Details')}
      </Text>
      {message && (
        <Text size='small' color='error'>
          {message}
        </Text>
      )}
      {violations.length > 0 && (
        <ul css={listStyle}>
          {violations.map((v, i) => (
            <li key={i}>
              <Text size='small'>
                {v.field && <strong>{v.field}: </strong>}
                {v.description}
              </Text>
              {v.hint && (
                <Text size='small' color='subdued'>
                  {v.hint}
                </Text>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const containerStyle = css({
  marginTop: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
});

const listStyle = css({
  margin: `${theme.spacing(0.5)} 0 0 0`,
  paddingLeft: theme.spacing(2.5),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
});
